import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, Plus, AlertTriangle, Clock, FileText, UserCheck, UserPlus } from 'lucide-react';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import type { CampaignWithComputed, CampaignStatus, DeliveryStatus, CompanyInvoiceStatus, InfluencerInvoiceStatus, MohitInvoice, InfluencerInvoice, CampaignExpense } from '../types/campaign';
import { parseJsonArray } from '../lib/jsonColumn';
import { reportFirstMissingField } from '../utils/requiredFields';
import { campaignService } from '../services/campaignService';
import { campaignAccessService } from '../services/campaignAccessService';
import { campaignInfluencerService } from '../services/campaignInfluencerService';
import { clientService } from '../services/clientService';
import { influencerService } from '../services/influencerService';
import { invoiceService } from '../services/invoiceService';
import { useAuth } from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';
import type { Client } from '../types/client';
import type { Influencer } from '../types/influencer';
import { MohitInvoices, type InvoiceInfluencerCommission } from './MohitInvoices';
import { RowList, ExpandToggle } from './ui/ListRows';
import { FormBlock } from './ui/FormBlock';
import { AdminOnlyToggle } from './AdminOnlyToggle';
import { DeliverableRow } from './DeliverableRow';
import { InfluencerInvoices } from './InfluencerInvoices';
import { CampaignExpenses } from './CampaignExpenses';
import { CampaignActivityLog } from './CampaignActivityLog';
import { useLanguage } from '../contexts/LanguageContext';
import { useStatusChangeNotification } from '../hooks/useStatusChangeNotification';
import { useAgencyDefaultCommission } from '../hooks/useAgencyDefaultCommission';
import { AGENCY_DEFAULT_COMMISSION_FALLBACK } from '../services/settingsService';
import { formatCurrency } from '../utils/currency';
import { reconcilePayables } from '../utils/payableReconcile';
import { calculateCampaignStatusPreview } from '../lib/status';
import { getPayableReadinessForCampaign } from '../utils/payableReadiness';
import { getNextAvencaMonth, isAvencaEnded, toMonthKey, formatMonthPt, getAvencaMissingMonths } from '../utils/avenca';
import { log } from '../lib/logger';

interface CampaignFormProps {
  campaign?: CampaignWithComputed | null;
  mode?: 'brand_action' | 'services';
  onClose: () => void;
  onSave: (saved?: CampaignWithComputed) => void;
}

export function CampaignForm({ campaign, mode = 'brand_action', onClose, onSave }: CampaignFormProps) {
  const { user, isAdmin } = useAuth();
  const { t, ts, translateError } = useLanguage();
  const { toast, showConfirm } = useModal();
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(false);
  const agentName = user?.user_metadata?.full_name || user?.email || 'Desconhecido';

  const parseDeliverables = (deliverablesStr: string | null | undefined): Array<{ content: string; date: string; status: DeliveryStatus }> => {
    if (!deliverablesStr) return mode === 'services' ? [] : [{ content: '', date: '', status: 'Draft' as DeliveryStatus }];
    try {
      const parsed = JSON.parse(deliverablesStr);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map(d => {
          let content = d.content || '';
          if (!content && (d.platform || d.type)) {
            const parts = [d.type, d.platform].filter(Boolean);
            content = parts.join(' ');
          }
          return {
            content,
            date: d.date || '',
            status: d.status || 'Draft'
          };
        });
      }
      return [{ content: '', date: '', status: 'Draft' as DeliveryStatus }];
    } catch {
      return [{ content: deliverablesStr, date: campaign?.delivery_date || '', status: (campaign?.delivery_status || 'Draft') as DeliveryStatus }];
    }
  };

  const parseMohitInvoices = (invoicesStr: string | null | undefined, fallbackData: any): MohitInvoice[] => {
    // Stored either as a JSON string or as an array (older rows)
    const parsed = parseJsonArray<MohitInvoice>(invoicesStr);
    if (parsed.length > 0) return parsed;

    // A new campaign starts without invoices: they are requested later
    if (!fallbackData) return [];

    return [{
      id: `temp-${crypto.randomUUID()}`,
      status: fallbackData?.company_invoice_status || 'Not Issued',
      invoice_number: fallbackData?.company_invoice_number || '',
      purchase_order: fallbackData?.company_purchase_order || '',
      issued_date: fallbackData?.company_invoice_issued_date || '',
      due_date: fallbackData?.company_invoice_due_date || '',
      amount_received: fallbackData?.company_amount_received || 0,
      payment_received_date: fallbackData?.company_payment_received_date || '',
      client_invoice_number: fallbackData?.client_invoice_number || '',
    }];
  };

  const parseInfluencerInvoices = (invoicesStr: string | null | undefined, fallbackData: any): InfluencerInvoice[] => {
    if (invoicesStr) {
      try {
        const parsed = JSON.parse(invoicesStr);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      } catch {
        // Fall through to create from old fields
      }
    }

    return [{
      id: `temp-${crypto.randomUUID()}`,
      invoice_number: fallbackData?.influencer_invoice_number || '',
      received_date: fallbackData?.influencer_invoice_received_date || '',
      amount: fallbackData?.influencer_amount_paid || 0,
      status: (fallbackData?.influencer_invoice_status || 'Not Requested') as InfluencerInvoiceStatus,
      payment_date: fallbackData?.influencer_payment_date || '',
    }];
  };

  const [deliverables, setDeliverables] = useState<Array<{ content: string; date: string; status: DeliveryStatus }>>(
    parseDeliverables(campaign?.deliverables)
  );

  const [mohitInvoices, setMohitInvoices] = useState<MohitInvoice[]>(
    parseMohitInvoices((campaign as any)?.mohit_invoices, campaign)
  );

  const [campaignExpenses, setCampaignExpenses] = useState<CampaignExpense[]>(
    parseJsonArray<CampaignExpense>((campaign as any)?.campaign_expenses)
  );

  const [influencerInvoices, setInfluencerInvoices] = useState<InfluencerInvoice[]>(
    !campaign && mode === 'services' ? [] : parseInfluencerInvoices((campaign as any)?.influencer_invoices, campaign)
  );

  const [hasCommission, setHasCommission] = useState<boolean>(() => {
    if (mode !== 'services') return true;
    if (!campaign) return false;
    const ct = (campaign as any)?.commission_type || 'flat_fee';
    if (ct === 'flat_fee' && ((campaign as any)?.commission_flat_fee || 0) > 0) return true;
    if (ct === 'percentage' && (campaign?.commission_pct || 0) > 0) return true;
    return false;
  });

  // Services are sometimes delivered with no supplier cost at all, so the
  // payable invoices are opt-in there (always present for brand actions).
  const [hasPayables, setHasPayables] = useState<boolean>(() => {
    if (mode !== 'services') return true;
    if (!campaign) return false;
    const stored = (campaign as any)?.has_payables;
    if (typeof stored === 'boolean') return stored;
    // Campaigns saved before the flag existed: infer from what they hold
    return parseJsonArray<InfluencerInvoice>((campaign as any)?.influencer_invoices).some(
      (inv) => (inv.amount || 0) > 0
        || (inv.source_name || '').trim() !== ''
        || (inv.invoice_number || '').trim() !== ''
    );
  });

  // Services: a fixed Valor, or an avença billed every month
  const [billingType, setBillingType] = useState<'fixed' | 'monthly'>(
    () => ((campaign as any)?.billing_type === 'monthly' ? 'monthly' : 'fixed')
  );
  const [monthlyAmount, setMonthlyAmount] = useState<number>(() => Number((campaign as any)?.monthly_amount) || 0);
  const [billingStart, setBillingStart] = useState<string>(() => toMonthKey((campaign as any)?.billing_start) || toMonthKey(new Date()) || '');
  const [billingEnd, setBillingEnd] = useState<string>(() => toMonthKey((campaign as any)?.billing_end) || '');
  const isMonthly = mode === 'services' && billingType === 'monthly';

  const [formData, setFormData] = useState({
    year: campaign?.year || currentYear,
    work_code: campaign?.work_code || '',
    client: campaign?.client || '',
    brand: campaign?.brand || '',
    campaign_ref: campaign?.campaign_ref || '',
    influencer: campaign?.influencer || '',
    campaign_status: ((campaign as any)?.campaign_status || 'Consultation') as CampaignStatus,
    deliverables: campaign?.deliverables || '',
    delivery_date: campaign?.delivery_date || '',
    delivery_status: (campaign?.delivery_status || 'Draft') as DeliveryStatus,
    fee: campaign?.fee || 0,
    commission_pct: campaign?.commission_pct ?? (mode === 'services' ? 0 : AGENCY_DEFAULT_COMMISSION_FALLBACK),
    commission_type: ((campaign as any)?.commission_type || (mode === 'services' ? 'flat_fee' : 'percentage')) as 'percentage' | 'flat_fee',
    commission_flat_fee: (campaign as any)?.commission_flat_fee || 0,
    currency: (campaign as any)?.currency || 'EUR',
    company_invoice_status: (campaign?.company_invoice_status || 'Not Issued') as CompanyInvoiceStatus,
    company_invoice_number: campaign?.company_invoice_number || '',
    company_purchase_order: campaign?.company_purchase_order || '',
    company_invoice_issued_date: campaign?.company_invoice_issued_date || '',
    company_invoice_due_date: campaign?.company_invoice_due_date || '',
    company_amount_received: campaign?.company_amount_received || 0,
    company_payment_received_date: campaign?.company_payment_received_date || '',
    client_invoice_number: campaign?.client_invoice_number || '',
    influencer_invoice_status: (campaign?.influencer_invoice_status || 'Not Requested') as InfluencerInvoiceStatus,
    influencer_invoice_number: campaign?.influencer_invoice_number || '',
    influencer_invoice_received_date: campaign?.influencer_invoice_received_date || '',
    influencer_payment_date: campaign?.influencer_payment_date || '',
    influencer_amount_paid: campaign?.influencer_amount_paid || 0,
    agent_name: campaign?.agent_name || agentName,
    admin_only: (campaign as any)?.admin_only ?? false,
    notes: (campaign as any)?.notes || '',
  });

  const [clients, setClients] = useState<string[]>([]);
  const [clientDetails, setClientDetails] = useState<Client | null>(null);
  const [influencerDetails, setInfluencerDetails] = useState<Influencer | null>(null);
  const [originalYear, setOriginalYear] = useState(campaign?.year || currentYear);
  // True once this form has created the record, so its saved year becomes the baseline
  const createdHereRef = useRef(false);
  const [yearChanged, setYearChanged] = useState(false);
  const [generatingWorkCode, setGeneratingWorkCode] = useState(false);
  // MOLONI INTEGRATION TEMPORARILY DISABLED
  // const [influencerIban, setInfluencerIban] = useState('');
  // const [isMoloniConnected, setIsMoloniConnected] = useState(false);
  // const [useManualEntry, setUseManualEntry] = useState(false);
  // Deliverables open collapsed once everything is published: the work is
  // done and the invoices below are what still needs attention
  const allDeliverablesPublished = deliverables.length > 0 && deliverables.every(d => d.status === 'Published');
  const [expandedSections, setExpandedSections] = useState({
    deliverables: mode !== 'services' && !allDeliverablesPublished,
    clientInvoices: false,
    influencerInvoices: false
  });
  const [activeTab, setActiveTab] = useState<'details' | 'activity'>('details');
  const [mohitInvoiceErrors, setMohitInvoiceErrors] = useState<Set<string>>(new Set());
  const [influencerInvoiceErrors, setInfluencerInvoiceErrors] = useState<Set<string>>(new Set());
  const [mohitTotalMismatch, setMohitTotalMismatch] = useState<{ expected: number; actual: number } | null>(null);
  const [influencerTotalMismatch, setInfluencerTotalMismatch] = useState<{ expected: number; actual: number } | null>(null);

  // Creator autocomplete state
  const [creatorSuggestions, setCreatorSuggestions] = useState<Influencer[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isExactMatch, setIsExactMatch] = useState(false);
  const [saveNewCreator, setSaveNewCreator] = useState(false);
  const creatorInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useStatusChangeNotification(campaign?.id || null, formData.campaign_status);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const clientsData = await clientService.getAll();
        setClients(clientsData.map(c => c.name));
      } catch (err) {
        log.error('Failed to load data:', err);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const loadClientDetails = async () => {
      if (formData.client) {
        const client = await clientService.getByName(formData.client);
        setClientDetails(client);
      } else {
        setClientDetails(null);
      }
    };
    loadClientDetails();
  }, [formData.client]);

  useEffect(() => {
    const loadInfluencerDetails = async () => {
      if (formData.influencer) {
        const influencer = await influencerService.getByName(formData.influencer);
        setInfluencerDetails(influencer);
        // MOLONI INTEGRATION TEMPORARILY DISABLED
        // setInfluencerIban(influencer?.iban || '');
      } else {
        setInfluencerDetails(null);
        // MOLONI INTEGRATION TEMPORARILY DISABLED
        // setInfluencerIban('');
      }
    };
    loadInfluencerDetails();
  }, [formData.influencer]);

  // ── Stay-open save ────────────────────────────────────────────────────────
  // After the first save this form keeps editing the record it created, so a
  // second save updates instead of duplicating.
  const [persistedId, setPersistedId] = useState<string | null>(campaign?.id ?? null);
  const isNewCampaign = !campaign && !persistedId;

  useEffect(() => {
    // The page hands back the record this form created; adopt its year as the
    // baseline so the year-change logic doesn't claim a second work code
    if (campaign && createdHereRef.current && originalYear !== campaign.year) {
      setOriginalYear(campaign.year);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  // ── Unsaved changes guard ────────────────────────────────────────────────
  // Snapshot of everything the user can edit; work_code is generated, not typed.
  const serializeFormState = () => JSON.stringify({
    formData: { ...formData, work_code: undefined },
    deliverables, mohitInvoices, influencerInvoices, campaignExpenses, hasCommission, hasPayables,
    billingType, monthlyAmount, billingStart, billingEnd,
  });
  const dirtySnapshotRef = useRef<string | null>(null);
  const [snapshotVersion, setSnapshotVersion] = useState(0);
  useEffect(() => {
    dirtySnapshotRef.current = serializeFormState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotVersion]);
  const isDirty = () => dirtySnapshotRef.current !== null && dirtySnapshotRef.current !== serializeFormState();

  const requestClose = async () => {
    if (isDirty()) {
      const discard = await showConfirm({
        title: t('form.discardChanges.title'),
        message: t('form.discardChanges.message'),
        confirmText: t('common.discard'),
        cancelText: t('common.cancel'),
        variant: 'warning',
      });
      if (!discard) return;
    }
    onClose();
  };

  // ── Default commission ───────────────────────────────────────────────────
  const agencyDefaultCommission = useAgencyDefaultCommission();

  // A brand-new campaign with no creator chosen starts at the agency default
  useEffect(() => {
    if (!isNewCampaign || mode === 'services' || agencyDefaultCommission == null || influencerDetails) return;
    const wasClean = !isDirty();
    setFormData(prev =>
      prev.commission_type === 'percentage' && prev.commission_pct === AGENCY_DEFAULT_COMMISSION_FALLBACK
        ? { ...prev, commission_pct: agencyDefaultCommission }
        : prev
    );
    if (wasClean) setSnapshotVersion(v => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyDefaultCommission, isNewCampaign, mode, influencerDetails]);

  // Picking a creator applies their own default (e.g. Bernardo's 20%)
  useEffect(() => {
    if (!isNewCampaign || mode === 'services' || !influencerDetails) return;
    setFormData(prev => ({
      ...prev,
      commission_type: influencerDetails.default_commission_type ?? prev.commission_type,
      commission_pct: influencerDetails.default_commission_pct ?? prev.commission_pct,
      commission_flat_fee: influencerDetails.default_commission_flat_fee ?? prev.commission_flat_fee,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [influencerDetails, isNewCampaign, mode]);

  // Debounced creator search
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const term = formData.influencer.trim();
    if (term.length < 2) {
      setCreatorSuggestions([]);
      setShowSuggestions(false);
      setIsExactMatch(false);
      setSaveNewCreator(false);
      return;
    }
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await influencerService.searchByName(term);
        setCreatorSuggestions(results);
        const exact = results.some(r => r.full_name.toLowerCase() === term.toLowerCase());
        setIsExactMatch(exact);
        if (exact) setSaveNewCreator(false);
      } catch (err) {
        log.error('Creator search failed:', err);
        setCreatorSuggestions([]);
      }
    }, 250);
    return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); };
  }, [formData.influencer]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        creatorInputRef.current && !creatorInputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!campaign && formData.client && formData.year) {
      setGeneratingWorkCode(true);
      campaignService.generateNextWorkCode(formData.year).then(code => {
        setFormData(prev => ({ ...prev, work_code: code }));
      }).catch(err => {
        log.error('Failed to generate work code:', err);
      }).finally(() => {
        setGeneratingWorkCode(false);
      });
    }
  }, [formData.client, formData.year, campaign]);

  useEffect(() => {
    if (campaign && formData.year !== originalYear) {
      setYearChanged(true);
      setGeneratingWorkCode(true);
      campaignService.generateNextWorkCode(formData.year).then(code => {
        setFormData(prev => ({ ...prev, work_code: code }));
      }).catch(err => {
        log.error('Failed to generate work code:', err);
      }).finally(() => {
        setGeneratingWorkCode(false);
      });
    } else if (campaign && formData.year === originalYear) {
      setYearChanged(false);
      setFormData(prev => ({ ...prev, work_code: campaign.work_code }));
    }
  }, [formData.year, campaign, originalYear]);

  // The Valor already includes any pass-through expense (it is what the
  // client pays). That part goes to the creator untouched, so the
  // commission applies to the Valor minus pass-through only.
  const passthroughTotal = campaignExpenses
    .filter(e => e.kind === 'passthrough')
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const commissionableFee = Math.max(0, formData.fee - passthroughTotal);
  const commissionAmount = formData.commission_type === 'flat_fee'
    ? formData.commission_flat_fee
    : commissionableFee * formData.commission_pct;
  const totalClientValue = formData.fee;
  // What the agency keeps: a service with no costs is all revenue; one
  // marked without commission keeps nothing to split across invoices
  const agencyCommissionTotal = mode === 'services' && !hasPayables
    ? totalClientValue
    : mode === 'services' && !hasCommission ? 0 : commissionAmount;
  const netFeeInfluencer = formData.fee - commissionAmount;
  // Payable tranches are what the creator invoices: the net fee. Deductions
  // (costs the creator bears) are withheld from a transfer and then marked
  // as settled with it, so they never shrink the tranches themselves.
  const deductionsTotal = campaignExpenses
    .filter(e => e.kind !== 'passthrough')
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const creatorPayableTotal = Math.max(0, netFeeInfluencer);
  const creatorReceivesTotal = Math.max(0, netFeeInfluencer - deductionsTotal);

  const availableInvoiceNames = useMemo(() => {
    if (!formData.influencer || formData.influencer.trim() === '') return [];
    return [{
      name: formData.influencer,
      type: 'influencer' as const,
      suggestedAmount: Math.round(creatorPayableTotal * 100) / 100,
      sourceId: influencerDetails?.id,
    }];
  }, [formData.influencer, creatorPayableTotal, influencerDetails]);

  // The database sets the status of creator campaigns from deliverables and
  // invoices when saving; preview the same rule so the chip follows edits
  // (e.g. a deliverable moved to In Production) instead of the stale value.
  const previewStatus = useMemo(() => {
    const payables = hasPayables ? influencerInvoices : [];
    return calculateCampaignStatusPreview({
      requiredFieldsReady: true,
      // the database treats Rejected like Draft
      deliverableStatuses: deliverables.map(d => (d.status === ('Rejected' as typeof d.status) ? 'Draft' : d.status || 'Draft')),
      hasIssuedInvoices:
        mohitInvoices.some(inv => inv.status !== 'Paid' && inv.status !== 'Cancelled' && inv.status !== ('Canceled' as typeof inv.status))
        || payables.some(inv => inv.status !== 'Paid'),
      allCompanyInvoicesPaidOrCancelled: mohitInvoices.every(inv => inv.status === 'Paid' || inv.status === 'Cancelled' || inv.status === ('Canceled' as typeof inv.status)),
      allInfluencerInvoicesPaid: payables.every(inv => inv.status === 'Paid'),
    });
  }, [deliverables, mohitInvoices, influencerInvoices, hasPayables]);
  const shownStatus = mode === 'services' ? formData.campaign_status : previewStatus;

  // Paga / Pronta a pagar / Aguarda cliente per payable, from the same money
  // rule as Pagamentos (what the client has paid funds payables in order)
  const payableReadiness = useMemo(() => {
    if (!hasPayables) return undefined;
    const noCommission = mode === 'services' && !hasCommission;
    return getPayableReadinessForCampaign({
      campaign_mode: mode === 'services' ? 'services' : 'brand_action',
      has_payables: hasPayables,
      fee: formData.fee,
      commission_type: noCommission ? 'flat_fee' : formData.commission_type,
      commission_pct: noCommission ? 0 : formData.commission_pct,
      commission_flat_fee: noCommission ? 0 : formData.commission_flat_fee,
      campaign_expenses: campaignExpenses,
      mohit_invoices: mohitInvoices,
      influencer_invoices: influencerInvoices,
    });
  }, [hasPayables, hasCommission, mode, formData.fee, formData.commission_type, formData.commission_pct, formData.commission_flat_fee, campaignExpenses, mohitInvoices, influencerInvoices]);

  // Invoices all settled while deliverables are still open usually means
  // someone forgot to mark them Published (the campaign then never completes)
  const paidButUnpublished = useMemo(() => {
    if (mode === 'services') return false;
    const real = deliverables.filter(d => d.content?.trim());
    const payables = hasPayables ? influencerInvoices : [];
    return real.some(d => d.status !== 'Published')
      && mohitInvoices.some(inv => inv.status === 'Paid')
      && mohitInvoices.every(inv => inv.status === 'Paid' || inv.status === 'Cancelled')
      && payables.every(inv => inv.status === 'Paid');
  }, [mode, deliverables, mohitInvoices, influencerInvoices, hasPayables]);

  // Avença: the Valor is what has been invoiced so far, so commission and
  // payouts keep working month by month
  const invoicedSoFar = mohitInvoices.reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
  useEffect(() => {
    if (!isMonthly) return;
    const rounded = Math.round(invoicedSoFar * 100) / 100;
    if (Math.abs((formData.fee || 0) - rounded) > 0.001) {
      setFormData(prev => ({ ...prev, fee: rounded }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMonthly, invoicedSoFar]);

  const avencaState = {
    campaign_mode: 'services',
    billing_type: 'monthly',
    billing_start: billingStart,
    billing_end: billingEnd || null,
    mohit_invoices: mohitInvoices,
  };
  const nextAvencaMonth = isMonthly ? getNextAvencaMonth(avencaState) : null;

  const generateAvencaMonth = () => {
    if (!nextAvencaMonth || monthlyAmount <= 0) return;
    const isBlank = (inv: MohitInvoice) =>
      !inv.period && !(inv.amount_received > 0) && !inv.invoice_number && inv.status === 'Not Issued';
    const clientId = `temp-${crypto.randomUUID()}`;
    setMohitInvoices(prev => [
      ...prev.filter(inv => !isBlank(inv)),
      {
        id: clientId,
        status: 'Not Issued',
        invoice_number: '',
        purchase_order: '',
        issued_date: '',
        due_date: '',
        amount_received: monthlyAmount,
        payment_received_date: '',
        client_invoice_number: '',
        period: nextAvencaMonth,
      },
    ]);
    if (hasPayables) {
      // The month's cost follows the month's invoice and is funded by it
      const pct = hasCommission && formData.commission_type === 'percentage' ? formData.commission_pct : 0;
      const monthlyNet = Math.round(monthlyAmount * (1 - pct) * 100) / 100;
      const isBlankPayable = (inv: InfluencerInvoice) =>
        !(inv.amount > 0) && !inv.invoice_number && inv.status !== 'Paid' && !(inv.funded_by_ids?.length);
      setInfluencerInvoices(prev => [
        ...prev.filter(inv => !isBlankPayable(inv)),
        {
          id: `temp-${crypto.randomUUID()}`,
          invoice_number: '',
          issue_date: '',
          due_date: '',
          amount: monthlyNet,
          status: 'Not Requested',
          payment_received_date: '',
          source_type: 'service',
          source_name: formData.influencer || '',
          funded_by_ids: [clientId],
        },
      ]);
    }
  };

  // On opening, saved payable tranches may no longer add up to what the
  // creator is owed (e.g. a deduction added after they were split). Re-split
  // the unpaid ones so this form, Pagamentos and the KPI all show the same
  // figure; the note explains the change and Guardar makes it permanent.
  const payablesReconciledRef = useRef(false);
  const [payableAdjustNote, setPayableAdjustNote] = useState<string | null>(null);
  useEffect(() => {
    if (payablesReconciledRef.current) return;
    payablesReconciledRef.current = true;
    if (!campaign?.id || !hasPayables) return;
    const result = reconcilePayables(influencerInvoices, creatorPayableTotal);
    if (!result) return;
    setInfluencerInvoices(result.invoices);
    setPayableAdjustNote(
      result.adjustments
        .map(a => `${t('form.payablesAdjusted.invoice').replace('{0}', String(a.index + 1))}: ${formatCurrency(a.from, formData.currency)} → ${formatCurrency(a.to, formData.currency)}`)
        .join(' · ')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistInvoiceOrder = useCallback(async (invoices: { id: string }[], type: 'Client' | 'Influencer') => {
    if (!campaign?.id) return;
    const existingIds = invoices
      .filter(inv => inv.id && !inv.id.startsWith('temp-') && inv.id.length === 36 && inv.id.includes('-'))
      .map(inv => inv.id);
    if (existingIds.length < 2) return;
    try {
      await invoiceService.reorderInvoices(campaign.id, type, existingIds);
    } catch {
    }
  }, [campaign?.id]);

  const handleMohitInvoicesChange = useCallback((invoices: MohitInvoice[]) => {
    setMohitInvoices(invoices);
    setMohitInvoiceErrors(new Set());
    setMohitTotalMismatch(null);
    persistInvoiceOrder(invoices, 'Client');
  }, [persistInvoiceOrder]);

  const handleInfluencerInvoicesChange = useCallback((invoices: InfluencerInvoice[]) => {
    setInfluencerInvoices(invoices);
    setInfluencerInvoiceErrors(new Set());
    setInfluencerTotalMismatch(null);
    persistInvoiceOrder(invoices, 'Influencer');
  }, [persistInvoiceOrder]);

  // Cross-check links live inside the influencer invoice JSON (funded_by_ids),
  // so they save together with the campaign — no extra table needed here.
  const crossCheckInvoiceLinks = useMemo(
    () => influencerInvoices.flatMap(inv =>
      (inv.funded_by_ids || []).map(clientId => ({
        id: `${inv.id}:${clientId}`,
        client_invoice_id: clientId,
        payable_invoice_id: inv.id,
        allocated_amount: null,
        created_at: '',
      }))
    ),
    [influencerInvoices]
  );

  const handleCrossCheckCreateLink = useCallback((payableId: string, clientId: string) => {
    setInfluencerInvoices(prev => prev.map(inv =>
      inv.id === payableId
        ? { ...inv, funded_by_ids: Array.from(new Set([...(inv.funded_by_ids || []), clientId])) }
        : inv
    ));
  }, []);

  const handleCrossCheckRemoveLink = useCallback((payableId: string, clientId: string) => {
    setInfluencerInvoices(prev => prev.map(inv =>
      inv.id === payableId
        ? { ...inv, funded_by_ids: (inv.funded_by_ids || []).filter(id => id !== clientId) }
        : inv
    ));
  }, []);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missingRequired = reportFirstMissingField([
      {
        key: 'campaign-brand',
        missing: !formData.brand || formData.brand.trim() === '',
        message: t('form.validation.brandRequired') || 'Brand is required',
      },
      {
        key: 'campaign-client',
        missing: !formData.client || formData.client.trim() === '',
        message: t('form.validation.clientRequired') || 'Client name is required',
      },
      {
        key: 'campaign-work-code',
        missing: (!formData.work_code || formData.work_code.trim() === '') && !!campaign && !yearChanged,
        message: t('form.validation.workCodeRequired') || 'Work code is required',
      },
      {
        key: 'campaign-fee',
        missing: !isMonthly && formData.fee <= 0,
        message: t('form.validation.feeRequired') || 'Fee must be greater than 0',
      },
      {
        key: 'campaign-monthly-amount',
        missing: isMonthly && monthlyAmount <= 0,
        message: t('avenca.validation.amount'),
      },
      {
        key: 'campaign-billing-start',
        missing: isMonthly && !billingStart,
        message: t('avenca.validation.start'),
      },
    ], toast.error);
    if (missingRequired) return;

    const mohitErrors = new Set<string>();
    mohitInvoices.forEach(inv => {
      if (inv.status === 'Paid' && (!inv.amount_received || inv.amount_received <= 0)) {
        mohitErrors.add(inv.id);
      }
    });

    const influencerErrors = new Set<string>();
    if (hasPayables) {
      influencerInvoices.forEach(inv => {
        if (inv.status === 'Paid' && (!inv.amount || inv.amount <= 0)) {
          influencerErrors.add(inv.id);
        }
      });
    }

    const paidMohitTotal = mohitInvoices
      .filter(inv => inv.status === 'Paid')
      .reduce((sum, inv) => sum + (inv.amount_received || 0), 0);
    const allMohitPaid = mohitInvoices.length > 0 && mohitInvoices.every(inv => inv.status === 'Paid');
    const mohitMismatch = allMohitPaid && Math.abs(paidMohitTotal - totalClientValue) > 0.01
      ? { expected: totalClientValue, actual: paidMohitTotal }
      : null;

    const paidInfluencerTotal = influencerInvoices
      .filter(inv => inv.status === 'Paid')
      .reduce((sum, inv) => sum + (inv.amount || 0), 0);
    const allInfluencerPaid = hasPayables && influencerInvoices.length > 0 && influencerInvoices.every(inv => inv.status === 'Paid');
    const expectedNet = Math.round(creatorPayableTotal * 100) / 100;
    const influencerMismatch = allInfluencerPaid && Math.abs(paidInfluencerTotal - expectedNet) > 0.01
      ? { expected: expectedNet, actual: paidInfluencerTotal }
      : null;

    setMohitInvoiceErrors(mohitErrors);
    setInfluencerInvoiceErrors(influencerErrors);
    setMohitTotalMismatch(mohitMismatch);
    setInfluencerTotalMismatch(influencerMismatch);

    const missingPaymentDate = mohitInvoices.filter(inv => inv.status === 'Paid' && !inv.payment_received_date);
    const firstMohitError = mohitInvoices.find(inv => mohitErrors.has(inv.id));
    const missingInvoiceField = reportFirstMissingField([
      {
        key: `mohit-invoice-amount-${firstMohitError?.id ?? ''}`,
        missing: mohitErrors.size > 0,
        message: 'Preencha o valor recebido para todas as faturas a receber marcadas como Paga',
        reveal: () => setExpandedSections(prev => ({ ...prev, clientInvoices: true })),
      },
      {
        key: `mohit-invoice-date-${missingPaymentDate[0]?.id ?? ''}`,
        missing: missingPaymentDate.length > 0,
        message: `A fatura nº ${missingPaymentDate.map(inv => inv.invoice_number || '(sem número)').join(', ')} está marcada como Paga mas não tem Data de Recebimento preenchida. Preencha a data para poder guardar.`,
        reveal: () => setExpandedSections(prev => ({ ...prev, clientInvoices: true })),
      },
    ], toast.error);
    if (missingInvoiceField) return;
    if (influencerErrors.size > 0) {
      setExpandedSections(prev => ({ ...prev, influencerInvoices: true }));
      toast.error('Preencha o valor pago para todas as faturas a pagar marcadas como Paga');
      return;
    }
    if (mohitMismatch) {
      setExpandedSections(prev => ({ ...prev, clientInvoices: true }));
      toast.error(
        `O total recebido (${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(mohitMismatch.actual)}) deve ser igual ao valor total da campanha (${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(mohitMismatch.expected)})`
      );
      return;
    }
    if (influencerMismatch) {
      setExpandedSections(prev => ({ ...prev, influencerInvoices: true }));
      toast.error(
        `O total pago (${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(influencerMismatch.actual)}) deve ser igual ao valor liquido (${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(influencerMismatch.expected)})`
      );
      return;
    }

    setLoading(true);

    try {

      try {
        const existingClient = await clientService.getByName(formData.client);
        if (!existingClient) {
          await clientService.create({
            name: formData.client,
            vat_number: null,
            address: null,
            contact_info: null
          });
        }
      } catch (clientErr) {
        log.warn('Auto-register client failed (likely permissions):', clientErr);
        toast.warning(t('warning.clientAutoRegisterFailed'));
      }

      // Everything paid but deliverables still open: the campaign would stay
      // Em Andamento / Faturação by mistake, so ask before saving.
      let deliverablesToSave = Array.isArray(deliverables) ? deliverables : [];
      if (mode !== 'services') {
        const realDeliverables = deliverablesToSave.filter(d => d.content?.trim());
        const payablesForCheck = hasPayables ? influencerInvoices : [];
        const clientsSettled = mohitInvoices.some(inv => inv.status === 'Paid')
          && mohitInvoices.every(inv => inv.status === 'Paid' || inv.status === 'Cancelled');
        const payablesSettled = payablesForCheck.every(inv => inv.status === 'Paid');
        if (realDeliverables.some(d => d.status !== 'Published') && clientsSettled && payablesSettled) {
          const markPublished = await showConfirm({
            title: t('form.unpublishedPaid.title'),
            message: t('form.unpublishedPaid.message'),
            confirmText: t('form.unpublishedPaid.confirm'),
            cancelText: t('form.unpublishedPaid.cancel'),
            variant: 'warning',
          });
          if (markPublished) {
            deliverablesToSave = deliverablesToSave.map(d =>
              d.content?.trim() ? { ...d, status: 'Published' as DeliveryStatus } : d
            );
            setDeliverables(deliverablesToSave);
          }
        }
      }

      // Convert empty date strings to null and serialize deliverables
      // Ensure deliverables is a valid array
      const validDeliverables = deliverablesToSave;
      const firstDeliverable = validDeliverables[0] || { date: '', status: 'Draft' as DeliveryStatus };

      // Calculate delivery_status based on ALL deliverables
      let calculatedDeliveryStatus: DeliveryStatus;
      if (validDeliverables.length === 0 || validDeliverables.every(d => !d.content)) {
        calculatedDeliveryStatus = 'Draft';
      } else if (validDeliverables.every(d => d.status === 'Published')) {
        calculatedDeliveryStatus = 'Published';
      } else if (validDeliverables.some(d => d.status === 'Published' || d.status === 'Approved')) {
        // Mixed statuses - find the "least complete" status
        const statuses = validDeliverables.map(d => d.status);
        if (statuses.includes('Draft')) calculatedDeliveryStatus = 'Draft';
        else if (statuses.includes('Production')) calculatedDeliveryStatus = 'Production';
        else if (statuses.includes('Rejected')) calculatedDeliveryStatus = 'Rejected';
        else if (statuses.includes('Delivered')) calculatedDeliveryStatus = 'Delivered';
        else if (statuses.includes('Approved')) calculatedDeliveryStatus = 'Approved';
        else calculatedDeliveryStatus = 'Published';
      } else {
        calculatedDeliveryStatus = firstDeliverable.status;
      }

      const sanitizedMohitInvoices = mohitInvoices.map(inv =>
        inv.status === 'Paid' ? inv : { ...inv, payment_received_date: '' }
      );

      const firstInvoice = sanitizedMohitInvoices[0] ?? null;
      const firstInfluencerInvoice = influencerInvoices[0] ?? null;

      const totalCompanyAmountReceived = sanitizedMohitInvoices
        .filter(inv => inv.status === 'Paid')
        .reduce((sum, inv) => sum + (inv.amount_received || 0), 0);

      const derivedCompanyStatus = (() => {
        if (sanitizedMohitInvoices.length === 0) return 'Not Issued';
        if (sanitizedMohitInvoices.length === 1) return firstInvoice.status;
        const allPaid = sanitizedMohitInvoices.every(inv => inv.status === 'Paid');
        if (allPaid) return 'Paid';
        const anyOverdue = sanitizedMohitInvoices.some(inv => inv.status === 'Overdue');
        if (anyOverdue) return 'Overdue';
        const anyPaid = sanitizedMohitInvoices.some(inv => inv.status === 'Paid');
        const anyIssued = sanitizedMohitInvoices.some(inv => inv.status === 'Issued');
        if (anyPaid || anyIssued) return 'Issued';
        return 'Not Issued';
      })();

      const totalInfluencerAmountPaid = influencerInvoices
        .filter(inv => inv.status === 'Paid')
        .reduce((sum, inv) => sum + (inv.amount || 0), 0);

      const derivedInfluencerStatus = (() => {
        if (influencerInvoices.length === 0) return 'Not Requested';
        if (influencerInvoices.length === 1) return firstInfluencerInvoice.status;
        const allPaid = influencerInvoices.every(inv => inv.status === 'Paid');
        if (allPaid) return 'Paid';
        const anyPaid = influencerInvoices.some(inv => inv.status === 'Paid');
        const anyReceived = influencerInvoices.some(inv => inv.status === 'Received');
        if (anyReceived || anyPaid) return 'Received';
        const anyRequested = influencerInvoices.some(inv => inv.status === 'Requested');
        if (anyRequested) return 'Requested';
        return 'Not Requested';
      })();

      const sanitizedData: any = {
        ...formData,
        campaign_mode: mode,
        influencer: mode === 'services' ? '' : formData.influencer,
        deliverables: JSON.stringify(validDeliverables),
        delivery_date: firstDeliverable.date || null,
        delivery_status: calculatedDeliveryStatus,
        mohit_invoices: JSON.stringify(sanitizedMohitInvoices),
        influencer_invoices: JSON.stringify(influencerInvoices),
        campaign_expenses: JSON.stringify(campaignExpenses.filter(e => e.description || e.amount > 0)),
        company_invoice_status: derivedCompanyStatus,
        company_invoice_number: firstInvoice?.invoice_number || null,
        company_purchase_order: firstInvoice?.purchase_order || null,
        company_invoice_issued_date: firstInvoice?.issued_date || null,
        company_invoice_due_date: firstInvoice?.due_date || null,
        company_amount_received: totalCompanyAmountReceived,
        company_payment_received_date: firstInvoice?.payment_received_date || null,
        client_invoice_number: firstInvoice?.client_invoice_number || null,
        influencer_invoice_status: derivedInfluencerStatus,
        influencer_invoice_number: firstInfluencerInvoice?.invoice_number || null,
        influencer_invoice_received_date: firstInfluencerInvoice?.received_date || null,
        influencer_amount_paid: totalInfluencerAmountPaid,
        influencer_payment_date: firstInfluencerInvoice?.payment_date || null,
        brand: formData.brand || null,
        campaign_ref: formData.campaign_ref || null,
        agent_id: user?.id || null,
        agent_name: formData.agent_name,
      };

      sanitizedData.has_payables = hasPayables;
      sanitizedData.billing_type = isMonthly ? 'monthly' : 'fixed';
      sanitizedData.monthly_amount = isMonthly ? monthlyAmount : null;
      sanitizedData.billing_start = isMonthly && billingStart ? `${billingStart}-01` : null;
      sanitizedData.billing_end = isMonthly && billingEnd ? `${billingEnd}-01` : null;

      if (!hasPayables) {
        sanitizedData.influencer_invoices = '[]';
        sanitizedData.influencer_invoice_status = 'Not Requested';
        sanitizedData.influencer_invoice_number = null;
        sanitizedData.influencer_invoice_received_date = null;
        sanitizedData.influencer_amount_paid = 0;
        sanitizedData.influencer_payment_date = null;
      }

      if (mode === 'services' && !hasCommission) {
        sanitizedData.commission_type = 'flat_fee';
        sanitizedData.commission_flat_fee = 0;
        sanitizedData.commission_pct = 0;
      }

      if (mode === 'services') {
        // The database no longer stages services (no deliverables to go on):
        // Consulta / Em Andamento come from the toggle, the rest from invoices.
        const clientStatuses = sanitizedMohitInvoices.map(inv => inv.status);
        const anyClientIssued = clientStatuses.some(st => st === 'Issued' || st === 'Overdue' || st === 'Paid');
        const allClientPaid = clientStatuses.length > 0 && clientStatuses.every(st => st === 'Paid');
        const payablesSettled = !hasPayables
          || (influencerInvoices.length > 0 && influencerInvoices.every(inv => inv.status === 'Paid'));
        // An avença is only complete once its last month has passed, been
        // invoiced and paid; while running, an unpaid invoice means Faturação
        const avencaOpen = isMonthly && (!isAvencaEnded(avencaState) || getAvencaMissingMonths(avencaState).length > 0);
        const anyClientUnpaid = clientStatuses.some(st => st === 'Issued' || st === 'Overdue');
        sanitizedData.campaign_status = allClientPaid && payablesSettled && !avencaOpen
          ? 'Completed'
          : (isMonthly ? anyClientUnpaid : anyClientIssued)
            ? 'Billing'
            : isMonthly && anyClientIssued
              ? 'In Progress'
              : formData.campaign_status === 'In Progress' ? 'In Progress' : 'Consultation';
      }

      // Remove campaign_status temporarily until column is added to database
      

      let savedCampaignId: string;

      const existingId = persistedId ?? campaign?.id ?? null;
      let savedRecord: CampaignWithComputed | null = null;

      if (existingId) {
        const updateData = { ...sanitizedData };
        if (yearChanged) {
          updateData._autoWorkCode = true;
        }
        savedRecord = await campaignService.update(existingId, updateData);
        savedCampaignId = existingId;
      } else {
        sanitizedData._autoWorkCode = true;
        const newCampaign = await campaignService.create(sanitizedData);
        savedRecord = newCampaign ?? null;
        savedCampaignId = newCampaign?.id || '';
        if (newCampaign?.id) {
          setPersistedId(newCampaign.id);
          createdHereRef.current = true;
          try {
            await campaignAccessService.grantAccessToAllUsers(newCampaign.id);
          } catch (accessError) {
            log.error('Error granting access to users:', accessError);
          }
        }
      }

      if (savedCampaignId && formData.influencer) {
        try {
          let influencerRecord = await influencerService.getByName(formData.influencer);

          if (!influencerRecord && saveNewCreator) {
            influencerRecord = await influencerService.create({ full_name: formData.influencer.trim() });
          }

          if (influencerRecord) {
            const existingInfluencers = await campaignInfluencerService.getByCampaign(savedCampaignId);
            const existingRecord = existingInfluencers.find(
              ci => ci.influencer_name?.toLowerCase() === formData.influencer.toLowerCase()
            );

            const syncCommissionPct = (mode === 'services' && !hasCommission) ? 0 : formData.commission_pct;
            const syncCommissionType = (mode === 'services' && !hasCommission) ? 'flat_fee' as const : formData.commission_type;
            const syncCommissionFlatFee = (mode === 'services' && !hasCommission) ? 0 : formData.commission_flat_fee;

            if (existingRecord) {
              await campaignInfluencerService.update(existingRecord.id, {
                agreed_fee: formData.fee,
                commission_pct: syncCommissionPct,
                commission_type: syncCommissionType,
                commission_flat_fee: syncCommissionFlatFee,
                currency: formData.currency,
                influencer_id: influencerRecord.id,
              });
            } else {
              await campaignInfluencerService.create({
                campaign_id: savedCampaignId,
                influencer_name: formData.influencer,
                influencer_id: influencerRecord.id,
                agreed_fee: formData.fee,
                commission_pct: syncCommissionPct,
                commission_type: syncCommissionType,
                commission_flat_fee: syncCommissionFlatFee,
                currency: formData.currency,
                status: 'Accepted' as const,
              });
            }
          }
        } catch (syncError) {
          log.error('Error syncing campaign_influencer:', syncError);
        }
      }

      toast.success(existingId ? t('form.campaign.updateSuccess') : t('form.campaign.createSuccess'));
      // Stay open: the list refreshes and this form now edits the saved
      // record — including the status the database just derived
      if (savedRecord?.campaign_status) {
        const savedStatus = savedRecord.campaign_status;
        setFormData(prev => ({ ...prev, campaign_status: savedStatus }));
      }
      setSnapshotVersion(v => v + 1);
      onSave(savedRecord ?? undefined);
    } catch (err) {
      log.error('[CampaignForm] Submit error:', err);
      toast.error(translateError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  return (
    <div className="cm-shell cm-shell--fullpage fixed inset-0 z-50 flex flex-col bg-white">

        {/* cm-header */}
        <div className="cm-header relative bg-white border-b border-gray-200 px-6 py-5">
          <div className="min-w-0">
            <h2 className="cm-title text-xl font-semibold text-gray-900">
              {campaign ? t('form.campaign.editTitle') : t('form.campaign.newTitle')}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-0.5">
              {formData.work_code && (
                <p className="cm-subtitle text-sm text-gray-500">{formData.work_code}</p>
              )}
              {isAdmin && (
                <AdminOnlyToggle
                  value={!!formData.admin_only}
                  onChange={(next) => setFormData(prev => ({ ...prev, admin_only: next }))}
                />
              )}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={requestClose}
            className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {campaign && (
            <nav className="cm-tabbar flex gap-1 mt-3" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'details'}
                onClick={() => setActiveTab('details')}
                className={`cm-tab flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === 'details'
                    ? 'cm-tab--active bg-gray-50 text-gray-900 border-b-2 border-accent-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FileText className="w-4 h-4" />
                {t('form.tab.details')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'activity'}
                onClick={() => setActiveTab('activity')}
                className={`cm-tab flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === 'activity'
                    ? 'cm-tab--active bg-gray-50 text-gray-900 border-b-2 border-accent-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Clock className="w-4 h-4" />
                {t('form.tab.activity')}
              </button>
            </nav>
          )}
        </div>

        {activeTab === 'activity' && campaign ? (
          <div className="cm-body flex-1 overflow-y-auto p-6 bg-gray-50">
            <CampaignActivityLog campaignId={campaign.id} />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 bg-gray-50">
            <div className="cm-body flex-1 overflow-y-auto p-6 space-y-7">

              {/* BLOCK: Informação base */}
              <FormBlock title={t('form.section.baseInfo') || 'Informação base'}>
                <div>
                  <div className="cm-grid grid grid-cols-1 md:grid-cols-2 gap-4">

                    {/* Agente — read-only */}
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.agent')} <span className="cm-req text-red-500">*</span>
                      </label>
                      <div className="cm-readonly px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                        {formData.agent_name}
                      </div>
                      <input type="hidden" name="agent_name" value={formData.agent_name} />
                    </div>

                    {/* Ano */}
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.year')} <span className="cm-req text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="year"
                        value={formData.year}
                        onChange={handleChange}
                        required
                        className="cm-input cm-numeric w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                      />
                    </div>

                    {/* Work Code — read-only when auto */}
                    <div className="cm-field md:col-span-2">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.workCode')} <span className="cm-req text-red-500">*</span>
                        {(!campaign || yearChanged) && <Badge variant="info" size="sm" className="ml-2">Automático</Badge>}
                      </label>
                      {(!campaign || yearChanged) ? (
                        <>
                          <div className="cm-readonly px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                            {generatingWorkCode ? 'Generating...' : formData.work_code}
                          </div>
                          <input type="hidden" name="work_code" value={formData.work_code} />
                        </>
                      ) : (
                        <input
                          type="text"
                          name="work_code"
                          data-required-field="campaign-work-code"
                          value={formData.work_code}
                          onChange={handleChange}
                          required
                          className="cm-input w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                        />
                      )}
                      {!campaign && (
                        <p className="cm-help text-xs text-gray-500 mt-1.5">{t('form.workCodeAutoGenerated')}</p>
                      )}
                      {yearChanged && (
                        <div className="flex items-center gap-1.5 mt-1.5 text-amber-600">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <p className="cm-help text-xs">Work code will be updated to match the new year</p>
                        </div>
                      )}
                    </div>

                    {/* Brand */}
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.brand')} <span className="cm-req text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="brand"
                        data-required-field="campaign-brand"
                        value={formData.brand}
                        onChange={handleChange}
                        required
                        placeholder={t('form.brandPlaceholder')}
                        className="cm-input w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                      />
                    </div>

                    {/* Cliente */}
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.client')} <span className="cm-req text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="client"
                        list="clients-list"
                        data-required-field="campaign-client"
                        value={formData.client}
                        onChange={handleChange}
                        required
                        placeholder={t('form.clientPlaceholder')}
                        className="cm-input w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                      />
                      <datalist id="clients-list">
                        {clients.map((client) => (
                          <option key={client} value={client} />
                        ))}
                      </datalist>
                      <p className="cm-help text-xs text-gray-500 mt-1 flex items-center gap-1">
                        {clients.includes(formData.client) ? (
                          <>
                            <span className="cm-chip cm-chip-neutral inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              Existing
                            </span>
                            {' '}{t('form.clientExisting')}
                          </>
                        ) : (
                          <>
                            <span className="cm-chip cm-chip-info inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                              Novo
                            </span>
                            {' '}{t('form.clientNew')}
                          </>
                        )}
                      </p>
                    </div>

                    {/* Campaign Ref */}
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.campaignRef')}
                      </label>
                      <input
                        type="text"
                        name="campaign_ref"
                        value={formData.campaign_ref}
                        onChange={handleChange}
                        placeholder={t('form.campaignRefPlaceholder')}
                        className="cm-input w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                      />
                    </div>

                    {/* Campaign Status */}
                    {mode === 'services' && (formData.campaign_status === 'Consultation' || formData.campaign_status === 'In Progress') ? (
                      // Services have no deliverables to stage them automatically:
                      // Consulta / Em Andamento are set here; Faturação and
                      // Concluída follow from the invoices on save.
                      <div className="cm-field">
                        <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                          {t('form.campaignStatus')} <span className="cm-req text-red-500">*</span>
                        </label>
                        <div className="inline-flex items-center bg-brand-tint rounded-full p-1" role="radiogroup" aria-label={t('form.campaignStatus')}>
                          {(['Consultation', 'In Progress'] as const).map((status) => {
                            const active = formData.campaign_status === status;
                            return (
                              <button
                                key={status}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                onClick={() => setFormData(prev => ({ ...prev, campaign_status: status }))}
                                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                                  active ? 'bg-brand-ink text-white shadow-sm' : 'text-gray-600 hover:text-brand-ink'
                                }`}
                              >
                                {ts(status)}
                              </button>
                            );
                          })}
                        </div>
                        <p className="cm-help text-[11px] text-gray-500 mt-1">{t('form.status.servicesHint')}</p>
                      </div>
                    ) : (
                      <div className="cm-field">
                        <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                          {t('form.campaignStatus')} <span className="cm-req text-red-500">*</span>
                          <Badge variant="neutral" size="sm" className="ml-2">Auto</Badge>
                        </label>
                        <div className="flex items-center gap-2">
                          <span className={`cm-chip ${
                            shownStatus === 'Consultation'
                              ? 'cm-chip-warning inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200'
                              : 'cm-chip-neutral inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700'
                          }`}>
                            {ts(shownStatus)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Influencer (hidden for services mode) */}
                    {mode !== 'services' && (
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.influencer')}
                        {formData.influencer.trim().length >= 2 && isExactMatch && (
                          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <UserCheck size={10} /> Existing
                          </span>
                        )}
                      </label>
                      <div className="relative">
                        <input
                          ref={creatorInputRef}
                          type="text"
                          name="influencer"
                          value={formData.influencer}
                          onChange={(e) => {
                            handleChange(e);
                            setShowSuggestions(true);
                          }}
                          onFocus={() => { if (creatorSuggestions.length > 0) setShowSuggestions(true); }}
                          placeholder={t('form.influencerPlaceholder')}
                          autoComplete="off"
                          className="cm-input w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                        />
                        {showSuggestions && creatorSuggestions.length > 0 && (
                          <div
                            ref={suggestionsRef}
                            className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                          >
                            {creatorSuggestions.map((s) => (
                              <button
                                key={s.id}
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between transition-colors"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, influencer: s.full_name }));
                                  setShowSuggestions(false);
                                  setIsExactMatch(true);
                                  setSaveNewCreator(false);
                                }}
                              >
                                <span className="font-medium text-gray-900">{s.full_name}</span>
                                {s.email && <span className="text-xs text-gray-400 ml-2 truncate">{s.email}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {formData.influencer.trim().length >= 2 && !isExactMatch && (
                        <label className="mt-2 flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={saveNewCreator}
                            onChange={(e) => setSaveNewCreator(e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-accent-600 focus:ring-accent-500"
                          />
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <UserPlus size={11} /> Save this creator
                          </span>
                        </label>
                      )}
                    </div>
                    )}

                    {/* Observations (services mode only) */}
                    {mode === 'services' && (
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.observations')}
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        placeholder={t('form.campaign.observationsPlaceholder')}
                        className="cm-input w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors resize-y"
                      />
                    </div>
                    )}

                    {/* Currency selector */}
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.currency') || 'Moeda'}
                      </label>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                        className="cm-select w-full py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                      >
                        <option value="EUR">EUR - Euro</option>
                        <option value="USD">USD - US Dollar</option>
                        <option value="GBP">GBP - British Pound</option>
                        <option value="BRL">BRL - Brazilian Real</option>
                        <option value="JPY">JPY - Japanese Yen</option>
                      </select>
                    </div>

                    {/* Services: fixed Valor or monthly avença */}
                    {mode === 'services' && (
                      <div className="cm-field md:col-span-2">
                        <div className="inline-flex items-center bg-brand-tint rounded-full p-1" role="radiogroup" aria-label={t('avenca.billingType')}>
                          {(['fixed', 'monthly'] as const).map(type => (
                            <button
                              key={type}
                              type="button"
                              role="radio"
                              aria-checked={billingType === type}
                              onClick={() => setBillingType(type)}
                              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                                billingType === type ? 'bg-brand-ink text-white shadow-sm' : 'text-gray-600 hover:text-brand-ink'
                              }`}
                            >
                              {t(type === 'fixed' ? 'avenca.fixed' : 'avenca.monthly')}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {isMonthly && (
                      <div className="cm-field md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                            {t('avenca.monthlyAmount')} <span className="cm-req text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            data-required-field="campaign-monthly-amount"
                            value={monthlyAmount || ''}
                            onChange={(e) => setMonthlyAmount(parseFloat(e.target.value) || 0)}
                            step="0.01"
                            min="0"
                            className="cm-input cm-numeric w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                            {t('avenca.start')} <span className="cm-req text-red-500">*</span>
                          </label>
                          <input
                            type="month"
                            data-required-field="campaign-billing-start"
                            value={billingStart}
                            onChange={(e) => setBillingStart(e.target.value)}
                            className="cm-input w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="cm-label block text-xs font-medium text-gray-600 mb-1">{t('avenca.end')}</label>
                          <input
                            type="month"
                            value={billingEnd}
                            min={billingStart || undefined}
                            onChange={(e) => setBillingEnd(e.target.value)}
                            className="cm-input w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                          />
                          <p className="cm-help text-[11px] text-gray-500 mt-1">{t('avenca.endHint')}</p>
                        </div>
                      </div>
                    )}

                    {/* Fee — currency affix left */}
                    {isMonthly ? (
                      <div className="cm-field">
                        <label className="cm-label block text-xs font-medium text-gray-600 mb-1">{t('avenca.invoicedSoFar')}</label>
                        <div className="cm-readonly px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-medium text-sm">
                          {formatCurrency(invoicedSoFar, formData.currency)}
                        </div>
                      </div>
                    ) : (
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.fee')} <span className="cm-req text-red-500">*</span>
                      </label>
                      <div className="cm-affix has-left relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none">
                          {{ EUR: '\u20AC', USD: '$', GBP: '\u00A3', BRL: 'R$', JPY: '\u00A5' }[formData.currency] || '\u20AC'}
                        </span>
                        <input
                          type="number"
                          name="fee"
                          data-required-field="campaign-fee"
                          value={formData.fee || ''}
                          onChange={handleChange}
                          step="0.01"
                          min="0"
                          required
                          className="cm-input cm-numeric w-full pl-8 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                        />
                      </div>
                    </div>
                    )}

                    {/* Commission opt-in toggle (services mode only) */}
                    {mode === 'services' && (
                      <div className="cm-field">
                        <label className="cm-label flex items-center gap-3 cursor-pointer select-none">
                          <span className="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200"
                            style={{ backgroundColor: hasCommission ? 'var(--color-accent-500, #3b82f6)' : '#d1d5db' }}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${hasCommission ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </span>
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={hasCommission}
                            onChange={(e) => {
                              const on = e.target.checked;
                              setHasCommission(on);
                              if (!on) {
                                setFormData(prev => ({ ...prev, commission_type: 'flat_fee' as const, commission_flat_fee: 0, commission_pct: 0 }));
                              }
                            }}
                          />
                          <span className="text-xs font-medium text-gray-700">
                            {t('form.campaign.hasCommission') || 'Esta campanha tem comissao?'}
                          </span>
                        </label>
                      </div>
                    )}

                    {/* Payables opt-in toggle (services mode only) */}
                    {mode === 'services' && (
                      <div className="cm-field">
                        <label className="cm-label flex items-center gap-3 cursor-pointer select-none">
                          <span className="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200"
                            style={{ backgroundColor: hasPayables ? 'var(--color-accent-500, #3b82f6)' : '#d1d5db' }}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${hasPayables ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </span>
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={hasPayables}
                            onChange={(e) => {
                              const on = e.target.checked;
                              setHasPayables(on);
                              if (on) {
                                // Seed a blank row so the section has something to fill in
                                if (influencerInvoices.length === 0) {
                                  setInfluencerInvoices([{
                                    id: `temp-${crypto.randomUUID()}`,
                                    invoice_number: '',
                                    issue_date: '',
                                    due_date: '',
                                    amount: 0,
                                    status: 'Not Requested',
                                    payment_received_date: '',
                                    source_type: 'service',
                                  }]);
                                }
                              } else {
                                const hasRealPayables = influencerInvoices.some(
                                  inv => (inv.amount || 0) > 0
                                    || (inv.source_name || '').trim() !== ''
                                    || (inv.invoice_number || '').trim() !== ''
                                );
                                if (hasRealPayables) {
                                  // Don't discard filled-in invoices without asking
                                  setHasPayables(true);
                                  void (async () => {
                                    const confirmed = await showConfirm({
                                      title: t('form.services.dropPayablesTitle'),
                                      message: t('form.services.dropPayablesMessage'),
                                      confirmText: t('common.remove') || 'Remover',
                                      cancelText: t('common.cancel'),
                                      variant: 'danger',
                                    });
                                    if (!confirmed) return;
                                    setHasPayables(false);
                                    setInfluencerInvoices([]);
                                    setInfluencerInvoiceErrors(new Set());
                                    setInfluencerTotalMismatch(null);
                                  })();
                                  return;
                                }
                                setInfluencerInvoices([]);
                                setInfluencerInvoiceErrors(new Set());
                                setInfluencerTotalMismatch(null);
                              }
                            }}
                          />
                          <span className="text-xs font-medium text-gray-700">
                            {t('form.services.hasPayables')}
                          </span>
                        </label>
                        <p className="cm-help text-[11px] text-gray-500 mt-1 ml-12">
                          {t('form.services.hasPayablesHint')}
                        </p>
                      </div>
                    )}

                    {/* Commission type toggle */}
                    {(mode !== 'services' || hasCommission) && (<>
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.commissionType') || 'Tipo de Comissao'}
                      </label>
                      <div className="flex rounded-xl overflow-hidden border border-gray-300 mb-2">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, commission_type: 'percentage' }))}
                          className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                            formData.commission_type === 'percentage'
                              ? 'bg-accent-500 text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {t('form.commission.percentage') || 'Percentagem'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, commission_type: 'flat_fee' }))}
                          className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                            formData.commission_type === 'flat_fee'
                              ? 'bg-accent-500 text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {t('form.commission.flatFee') || 'Valor Fixo'}
                        </button>
                      </div>

                      {/* Commission value input */}
                      {formData.commission_type === 'flat_fee' ? (
                        <>
                          <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                            {t('form.campaign.commissionAmount') || 'Valor da Comissao'} <span className="cm-req text-red-500">*</span>
                          </label>
                          <div className="cm-affix has-left relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none">
                              {{ EUR: '\u20AC', USD: '$', GBP: '\u00A3', BRL: 'R$', JPY: '\u00A5' }[formData.currency] || '\u20AC'}
                            </span>
                            <input
                              type="number"
                              value={formData.commission_flat_fee || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, commission_flat_fee: parseFloat(e.target.value) || 0 }))}
                              step="0.01"
                              min="0"
                              required
                              className="cm-input cm-numeric w-full pl-8 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                            {t('form.campaign.commissionPct')} <span className="cm-req text-red-500">*</span>
                          </label>
                          <div className="cm-affix has-right relative">
                            <input
                              type="number"
                              name="commission_pct"
                              value={formData.commission_pct * 100 || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, commission_pct: parseFloat(e.target.value) / 100 || 0 }))}
                              step="1"
                              min="0"
                              max="100"
                              required
                              className="cm-input cm-numeric w-full pl-4 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none">%</span>
                          </div>
                          <p className="cm-help text-xs text-gray-500 mt-1">{t('form.commissionPctHelp')}</p>
                        </>
                      )}
                    </div>

                    {/* Commission Calculated — accent readonly */}
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.commissionValue')}
                      </label>
                      <div className="cm-readonly cm-readonly--accent px-3 py-2 border border-accent-200 rounded-lg bg-accent-50 text-accent-700 font-semibold text-sm">
                        {formatCurrency(commissionAmount, formData.currency)}
                      </div>
                      {passthroughTotal > 0 && formData.commission_type !== 'flat_fee' && (
                        <p className="cm-help text-[11px] text-gray-500 mt-1">
                          {t('form.campaign.commissionOnFee')
                            .replace('{0}', `${Math.round(formData.commission_pct * 10000) / 100}%`)
                            .replace('{1}', formatCurrency(commissionableFee, formData.currency))}
                        </p>
                      )}
                    </div>

                    {/* Net Fee — readonly */}
                    <div className="cm-field">
                      <label className="cm-label block text-xs font-medium text-gray-600 mb-1">
                        {t('form.campaign.netFee')}
                      </label>
                      <div className="cm-readonly px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-medium text-sm">
                        {formatCurrency(netFeeInfluencer, formData.currency)}
                      </div>
                      {passthroughTotal > 0 && (
                        <p className="cm-help text-[11px] font-medium text-amber-700 mt-1">
                          {t('form.campaign.netFeeIncludesExpenses').replace(
                            '{0}',
                            formatCurrency(passthroughTotal, formData.currency),
                          )}
                        </p>
                      )}
                      {deductionsTotal > 0 && (
                        <p className="cm-help text-[11px] font-medium text-amber-700 mt-1">
                          {t('form.campaign.netFeeAfterDeductions')
                            .replace('{0}', formatCurrency(deductionsTotal, formData.currency))
                            .replace('{1}', formatCurrency(creatorReceivesTotal, formData.currency))}
                        </p>
                      )}
                    </div>
                    </>)}

                  </div>
                </div>
              </FormBlock>

              {/* BLOCK: Entregáveis */}
              <FormBlock title={t('form.deliverables')} bodyClassName="px-5 pt-6 pb-4">
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={expandedSections.deliverables}
                  onClick={() => toggleSection('deliverables')}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('deliverables'); }
                  }}
                  className="group flex items-center justify-between gap-3 -mx-2 px-2 py-1 rounded-xl cursor-pointer select-none hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <ExpandToggle open={expandedSections.deliverables} />
                    <span className={`cm-chip ${allDeliverablesPublished ? 'cm-chip-success' : 'cm-chip-neutral'}`}>
                      {allDeliverablesPublished
                        ? t('form.deliverablesAllPublished').replace('{0}', String(deliverables.length))
                        : `${deliverables.filter(d => d.status === 'Published').length}/${deliverables.length} ${ts('Published').toLowerCase()}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="cm-btn-add"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeliverables([...deliverables, { content: '', date: '', status: 'Draft' }]);
                      if (!expandedSections.deliverables) toggleSection('deliverables');
                    }}
                  >
                    <Plus className="w-4 h-4" /> {t('form.addDeliverable')}
                  </button>
                </div>
                {expandedSections.deliverables && (
                  <div className="pt-4 space-y-3">
                    {paidButUnpublished && (
                      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
                        <div className="flex items-start gap-2 text-sm text-amber-800">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{t('form.unpublishedPaid.banner')}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setDeliverables(prev => prev.map(d =>
                            d.content?.trim() ? { ...d, status: 'Published' as DeliveryStatus } : d
                          ))}
                          className="px-3 py-1.5 rounded-full text-xs font-semibold bg-brand-ink text-white hover:bg-black transition-colors"
                        >
                          {t('form.unpublishedPaid.confirm')}
                        </button>
                      </div>
                    )}
                    {deliverables.length === 0 ? (
                      <p className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-4 text-center">
                        {t('form.noDeliverables')}
                      </p>
                    ) : (
                      <RowList>
                        {deliverables.map((deliverable, index) => {
                          const update = (patch: Partial<typeof deliverable>) => {
                            const updated = [...deliverables];
                            updated[index] = { ...updated[index], ...patch };
                            setDeliverables(updated);
                          };
                          return (
                            <DeliverableRow
                              key={index}
                              index={index}
                              count={deliverables.length}
                              content={deliverable.content}
                              date={deliverable.date}
                              status={deliverable.status}
                              statusOptions={[
                                { value: 'Draft', label: t('status.draft') },
                                { value: 'Production', label: t('status.production') },
                                { value: 'Delivered', label: t('status.delivered') },
                                { value: 'Approved', label: t('status.approved') },
                                { value: 'Rejected', label: t('status.rejected') },
                                { value: 'Published', label: t('status.published') },
                              ]}
                              contentPlaceholder={t('form.deliverablePlaceholder')}
                              onContent={(value) => update({ content: value })}
                              onDate={(value) => update({ date: value })}
                              onStatus={(value) => update({ status: value as DeliveryStatus })}
                              onMove={(direction) => {
                                const target = direction === 'up' ? index - 1 : index + 1;
                                if (target < 0 || target >= deliverables.length) return;
                                const updated = [...deliverables];
                                [updated[index], updated[target]] = [updated[target], updated[index]];
                                setDeliverables(updated);
                              }}
                              onRemove={deliverables.length > 1 ? () => setDeliverables(deliverables.filter((_, i) => i !== index)) : undefined}
                              labels={{
                                moveUp: t('form.moveUp'),
                                moveDown: t('form.moveDown'),
                                remove: t('form.removeDeliverable'),
                                published: t('status.published'),
                                fee: 'Fee',
                                commission: t('form.campaign.commissionValue'),
                              }}
                            />
                          );
                        })}
                      </RowList>
                    )}
                  </div>
                )}
              </FormBlock>

              {/* BLOCK: Fatura Mohit */}
                <MohitInvoices
                  invoices={mohitInvoices}
                  onChange={handleMohitInvoicesChange}
                  totalCommission={agencyCommissionTotal}
                  requests={{
                    campaignId: persistedId,
                    campaignName: [formData.work_code, formData.brand].filter(Boolean).join(' · ') || 'Campanha',
                    purchaseOrder: mohitInvoices.find(inv => inv.purchase_order)?.purchase_order,
                  }}
                  clientDetails={clientDetails}
                  totalAmount={totalClientValue}
                  avenca={isMonthly ? {
                    summary: t('avenca.summary')
                      .replace('{0}', formatCurrency(monthlyAmount, formData.currency))
                      .replace('{1}', formatMonthPt(billingStart))
                      .replace('{2}', billingEnd ? ` ${t('avenca.until')} ${formatMonthPt(billingEnd)}` : '')
                      .replace('{3}', String(mohitInvoices.filter(inv => inv.period).length))
                      .replace('{4}', formatCurrency(invoicedSoFar, formData.currency))
                      .replace('{5}', String(mohitInvoices.filter(inv => inv.period && inv.status === 'Paid').length)),
                    nextMonthLabel: nextAvencaMonth ? formatMonthPt(nextAvencaMonth) : null,
                    onGenerateNext: generateAvencaMonth,
                  } : null}
                  errorIds={mohitInvoiceErrors}
                  totalMismatch={mohitTotalMismatch}
                  influencerCommissions={formData.influencer ? [{
                    influencer_name: formData.influencer,
                    agreed_fee: formData.fee,
                    commission_pct: formData.commission_pct,
                    commission_flat_fee: 0,
                    commission_type: 'percentage',
                  } as InvoiceInfluencerCommission] : undefined}
                />

              {/* Payables (and therefore the cross-check) only exist when this
                  service actually has costs to pay */}
              {hasPayables && (
                <>
                  {payableAdjustNote && (
                    <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">{t('form.payablesAdjusted.title')}</p>
                        <p>
                          {t(deductionsTotal > 0 ? 'form.payablesAdjusted.body' : 'form.payablesAdjusted.bodyNoDeductions')
                            .replace('{0}', formatCurrency(creatorPayableTotal, formData.currency))
                            .replace('{1}', formatCurrency(deductionsTotal, formData.currency))}
                        </p>
                        <p className="font-medium mt-0.5">{payableAdjustNote}</p>
                      </div>
                    </div>
                  )}

                  {/* BLOCK: Faturas a Pagar (matching lives inside each invoice) */}
                    <InfluencerInvoices
                      invoices={influencerInvoices}
                      onChange={handleInfluencerInvoicesChange}
                      availableNames={availableInvoiceNames}
                      netFeeInfluencer={creatorPayableTotal}
                      readiness={payableReadiness}
                      deductionsToWithhold={campaignExpenses
                        .filter(e => e.kind !== 'passthrough' && !e.settled_in)
                        .reduce((sum, e) => sum + (e.amount || 0), 0)}
                      errorIds={influencerInvoiceErrors}
                      totalMismatch={influencerTotalMismatch}
                      totalCommission={commissionAmount}
                      clientInvoices={mohitInvoices}
                      invoiceLinks={crossCheckInvoiceLinks}
                      onCreateLink={handleCrossCheckCreateLink}
                      onRemoveLinkPair={handleCrossCheckRemoveLink}
                      allowUnsavedClientLinks
                      defaultSourceType={mode === 'services' ? 'service' : undefined}
                      hideWhenEmpty={mode === 'services'}
                      providerMode={mode === 'services'}
                    />
                </>
              )}

              {/* BLOCK: Despesas (deduções ocasionais) */}
              <CampaignExpenses
                expenses={campaignExpenses}
                onChange={setCampaignExpenses}
                paidPayables={hasPayables ? influencerInvoices
                  .map((inv, i) => ({ inv, i }))
                  .filter(({ inv }) => inv.status === 'Paid' && inv.id)
                  .map(({ inv, i }) => ({
                    id: inv.id,
                    label: `${t('form.payablesAdjusted.invoice').replace('{0}', String(i + 1))}${inv.payment_received_date ? ` · ${inv.payment_received_date.slice(0, 10).split('-').reverse().join('/')}` : ''}`,
                    date: inv.payment_received_date || null,
                  })) : undefined}
              />

            </div>

            {/* cm-footer */}
            <div className="cm-footer flex justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4">
              <Button
                type="button"
                variant="secondary"
                size="md"
                className="cm-btn cm-btn-secondary"
                onClick={requestClose}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="cm-btn cm-btn-primary"
                disabled={loading}
              >
                {loading ? t('common.saving') : (campaign ? t('common.update') : t('common.create'))}
              </Button>
            </div>
          </form>
        )}
    </div>
  );
}
